import { useState, useEffect, useMemo, useRef } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useProjects } from '../../hooks/useProjects';
import { usePreviewInvoice, useGenerateInvoice } from '../../hooks/useInvoices';
import { useProjectToBeInvoiced } from '../../hooks/useToBeInvoiced';
import { useBillingSchedule } from '../../hooks/useBillingPeriods';
import { useAuth } from '../../services/authContext';
import { Button } from '../../components/common/Button';
import { InvoiceBreakdownTable } from '../../components/invoices/InvoiceBreakdownTable';
import {
  InvoiceReviewPanel,
  defaultSelection,
  selectionHasBlockedItem,
  type ReviewSelection,
} from '../../components/invoices/InvoiceReviewPanel';
import type { InclusionDescriptor, InvoiceBreakdown } from '../../services/api/invoices';
import type { ProjectListItem } from '../../services/api/projects';
import { SLA_BALICEK_PAYMENT_TYPE_ID } from '../../constants/typeIds';
import { formatCurrency, formatDisplayDate } from '../../utils/format';
import { formatMonthYear } from '../../utils/dateFormat';
import { DateField } from '../../components/ui/DateField';

type BillingFilter = 'all' | 'balicek' | 'hourly';

/**
 * MKS-207 — the `YYYY-MM` period the review panel is about, or `null` when the
 * from/to range is not a single calendar month. Every readiness signal
 * (overage buckets, open timesheets, the "logged nothing" query) is defined
 * per month, so a multi-month range has no single answer and the panel stays
 * hidden rather than showing one month's figures for a longer invoice.
 */
export function periodFromRange(from: string, to: string): string | null {
  const fromMonth = /^(\d{4}-\d{2})-\d{2}$/.exec(from)?.[1];
  const toMonth = /^(\d{4}-\d{2})-\d{2}$/.exec(to)?.[1];
  if (!fromMonth || !toMonth || fromMonth !== toMonth) return null;
  return fromMonth;
}

/**
 * MKS-207 — canonical key of an inclusion descriptor, so the auto-preview
 * effect can tell "the operator moved a checkbox" from "React re-rendered".
 * `undefined` entryIds (no restriction) and `[]` (bill no entries) are
 * deliberately different keys — they mean opposite things.
 */
export function inclusionKey(inclusion: InclusionDescriptor | null): string {
  if (inclusion === null) return 'none';
  return JSON.stringify({
    package: inclusion.package ?? null,
    overage: inclusion.overage ?? null,
    receipts: [...(inclusion.expenseReceiptIds ?? [])].sort((a, b) => a - b),
    entries: inclusion.entryIds === undefined ? null : [...inclusion.entryIds].sort((a, b) => a - b),
  });
}

/** The selection the panel owns, in the shape the API expects. */
function toInclusion(selection: ReviewSelection): InclusionDescriptor {
  return {
    package: selection.package,
    overage: selection.overage,
    expenseReceiptIds: selection.expenseReceiptIds,
    entryIds: selection.entryIds,
  };
}

// Phase 110 (Plan 110-09, D-07) — client-side mirror of the server-side
// Fastify body pattern (backend/src/api/routes/invoices.ts) and the
// resolveDiscount parse/reject (backend/src/services/invoiceService.ts):
// whole numbers 0-100 only, no decimals, no sign. This is a convenience
// mirror so the operator learns before submitting — the server-side check
// is the real control, never this one.
const DISCOUNT_OVERRIDE_PATTERN = /^(100|[0-9]{1,2})$/;

// Phase 110 (Plan 110-12, 110-REVIEW CR-02) — canonical, order-independent
// key of an override payload. `undefined` and `{}` both mean "no overrides
// were sent," so they must produce the same key — otherwise the auto-preview
// effect (which sends no overrides at all) could never match a preview whose
// map was cleared back to empty by hand. The sort is defensive rather than
// load-bearing: JS already enumerates integer-like string keys in ascending
// numeric order, and relying on that engine behaviour silently would be a
// trap for anyone who later keys this map differently (e.g. non-numeric ids).
export function discountOverridesKey(payload: Record<string, string> | undefined): string {
  const entries = payload ? Object.entries(payload) : [];
  const sorted = entries.sort(([a], [b]) => Number(a) - Number(b));
  return JSON.stringify(sorted);
}

// Phase 110 (Plan 110-12, 110-REVIEW CR-02) — one sentence, two consumers
// (the amber notice below the actions row, and the `handleGenerate` guard's
// error banner) so the disabled control and the refusal can never drift into
// two different phrasings of the same condition.
const STALE_PREVIEW_MESSAGE =
  'Discount override changed since the last preview — click Preview to update the amounts before generating.';

// MKS-207 — one sentence for the confirmation checkbox and the `handleGenerate`
// refusal, for the same reason as STALE_PREVIEW_MESSAGE above.
const BLOCKED_CONFIRM_MESSAGE =
  'This invoice includes an item with no rate, which would be billed at 0. Confirm below before generating.';

// Quick task 260912-j8z (G-02, D-Gap2 FINAL) — decodes the block selection
// `ToBeInvoicedCard` carries in the URL (`sel=1&pkg=&ovg=&receiptIds=&entryIds=`)
// back into an `InclusionDescriptor`. Returns `null` for "nothing was carried"
// (today's un-carried behaviour: no `inclusion` sent, bill everything) AND for
// any malformed encoding — a partially-decoded descriptor is more dangerous
// than none, because an absent `inclusion` at least means "bill everything",
// the same as a normal un-carried visit. `entryIds` is decoded as `undefined`
// when the param key is entirely ABSENT (Hour Package projects never send
// it — the period's entries must stay linked for audit), distinct from an
// empty string (present but empty — "bill no entries").
function parseIdList(raw: string): number[] | null {
  if (raw === '') return [];
  const ids = raw.split(',').map((s) => parseInt(s.trim(), 10));
  if (ids.some((n) => Number.isNaN(n))) return null;
  return ids;
}

function decodeCarriedInclusion(
  searchParams: URLSearchParams,
): { inclusion: InclusionDescriptor; forProjectId: number } | null {
  if (searchParams.get('sel') !== '1') return null;

  const projectIdsStr = searchParams.get('projectIds');
  const projectIds = projectIdsStr
    ? projectIdsStr.split(',').map((id) => parseInt(id.trim(), 10)).filter((id) => !isNaN(id))
    : [];
  // A carried selection describes exactly ONE project (it was made on that
  // project's own To Be Invoiced card) — a multi-project URL cannot be a
  // valid carry, whatever produced it.
  if (projectIds.length !== 1) return null;

  const pkg = searchParams.get('pkg');
  const ovg = searchParams.get('ovg');
  if (pkg !== '0' && pkg !== '1') return null;
  if (ovg !== '0' && ovg !== '1') return null;

  const receiptIdsParam = searchParams.get('receiptIds');
  if (receiptIdsParam === null) return null;
  const expenseReceiptIds = parseIdList(receiptIdsParam);
  if (expenseReceiptIds === null) return null;

  let entryIds: number[] | undefined;
  if (searchParams.has('entryIds')) {
    const parsed = parseIdList(searchParams.get('entryIds') ?? '');
    if (parsed === null) return null;
    entryIds = parsed;
  } else {
    entryIds = undefined;
  }

  return {
    inclusion: { package: pkg === '1', overage: ovg === '1', expenseReceiptIds, entryIds },
    forProjectId: projectIds[0]!,
  };
}

// Compact, read-only English summary of what a carried selection covers —
// the operator must be able to see, before pressing Generate, what will be
// billed (that is the entire point of the hop from the project card).
function describeInclusion(inclusion: InclusionDescriptor): string {
  const parts: string[] = [];
  parts.push(inclusion.package ? 'Package included' : 'Package excluded');
  parts.push(inclusion.overage ? 'Overage included' : 'Overage excluded');
  const receiptCount = inclusion.expenseReceiptIds?.length ?? 0;
  parts.push(
    receiptCount > 0
      ? `${receiptCount} external cost receipt${receiptCount === 1 ? '' : 's'}`
      : 'No external costs',
  );
  if (inclusion.entryIds === undefined) {
    parts.push('all billable entries');
  } else if (inclusion.entryIds.length > 0) {
    parts.push(
      `${inclusion.entryIds.length} billable entr${inclusion.entryIds.length === 1 ? 'y' : 'ies'}`,
    );
  } else {
    parts.push('no billable entries');
  }
  return parts.join('; ');
}

export default function InvoiceGeneratePage() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  // Use same filter as detailed report page - only in_progress projects
  const { data: projects } = useProjects({ state: 'in_progress' });
  const previewMutation = usePreviewInvoice();
  const generateMutation = useGenerateInvoice();
  // MKS-207 — `/invoices/new` already sits behind `FinanceRoute`, so this is
  // true for everybody who can reach the page today. It is read from the auth
  // context anyway so the panel's read-only branch is a real, exercised path
  // rather than dead code the day the route opens up.
  const { isFinance } = useAuth();

  // Parse URL parameters
  const urlParams = useMemo(() => {
    const projectIdsStr = searchParams.get('projectIds');
    const projectIds = projectIdsStr
      ? projectIdsStr.split(',').map((id) => parseInt(id.trim(), 10)).filter((id) => !isNaN(id))
      : [];
    return {
      projectIds,
      from: searchParams.get('from') || '',
      to: searchParams.get('to') || '',
    };
  }, [searchParams]);

  // Quick task 260912-j8z (G-02, D-Gap2 FINAL) — the block selection carried
  // from `ToBeInvoicedCard`, decoded once from the URL on mount. `null` means
  // nothing was carried (or the encoding was malformed) — today's behaviour,
  // no `inclusion` sent, bill everything.
  const carried = useMemo(() => decodeCarriedInclusion(searchParams), [searchParams]);

  const [form, setForm] = useState({
    projectIds: [] as number[],
    from: '',
    to: '',
    name: '',
    invoiceNumber: '',
    orderNumber: '',
    // MKS-214 — due date entered by finance at issue time. Empty = no due
    // date, which leaves the invoice in the never-overdue state every
    // pre-MKS-214 invoice is in.
    dueDate: '',
  });
  const [preview, setPreview] = useState<InvoiceBreakdown | null>(null);

  // MKS-136 — which billing periods this invoice is for. Offered for ONE
  // selected project at a time: the schedule, its Fixed Price sum and its
  // phase boundaries are all per-project, and a multi-project invoice has no
  // single schedule to show.
  const [selectedPeriodIds, setSelectedPeriodIds] = useState<number[]>([]);
  const singleProjectId = form.projectIds.length === 1 ? form.projectIds[0] : undefined;
  const { data: scheduleData } = useBillingSchedule(singleProjectId);
  const readyPeriods = (scheduleData?.data.periods ?? []).filter(
    (p) => p.status === 'ready_to_invoice',
  );
  // Only ever send ids that are still on screen — a project switch must not
  // leave a stale period attached to the next invoice.
  const billingPeriodIds = selectedPeriodIds.filter((id) =>
    readyPeriods.some((p) => p.id === id),
  );
  const billingPeriodIdsPayload = billingPeriodIds.length > 0 ? billingPeriodIds : undefined;

  // MKS-256 — whether finance has typed into the Due Date field. A ref, not
  // state: `applyDueDateSuggestion` below runs inside `.then()` callbacks of
  // effects whose dependency arrays do not include it (the auto-preview
  // effect's deps are `[isInitialized]`; the inclusion-change effect's deps
  // are the inclusion key and the form range). A state value read in those
  // callbacks would be the one captured when the request was fired — the
  // same class of staleness bug this ticket fixes. A ref is read at call
  // time instead, and flipping it never re-runs a preview effect.
  const dueDateTouchedRef = useRef(false);

  // MKS-214 / MKS-256 — an untouched Due Date field is a live display of the
  // current preview's `defaultDueDate`, whichever of the three result sites
  // (auto-preview, manual Preview, MKS-207 inclusion-change re-preview)
  // produced it: every preview result calls this, and it always applies the
  // latest suggestion to an untouched field, including clearing it when the
  // current preview carries none. Leaving a stale date from a since-changed
  // project selection on screen would be this same bug in a different shape.
  // A date finance typed is never overwritten, no matter which path fires.
  // Clearing the field (the input's `onChange` below) hands control back to
  // the preview, and the next result re-suggests.
  const applyDueDateSuggestion = (result: InvoiceBreakdown) => {
    if (dueDateTouchedRef.current) return;
    const next = result.defaultDueDate ?? '';
    setForm((prev) => (prev.dueDate === next ? prev : { ...prev, dueDate: next }));
  };
  // Phase 110 (Plan 110-12, 110-REVIEW CR-02) — the canonical key of the
  // override payload that produced the `preview` currently held above. Only
  // meaningful while `preview` is non-null; set at every site that sets a
  // preview, and nowhere else.
  const [previewedOverridesKey, setPreviewedOverridesKey] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [projectFilter, setProjectFilter] = useState('');
  const [billingFilter, setBillingFilter] = useState<BillingFilter>('all');
  const [isInitialized, setIsInitialized] = useState(false);
  // Phase 110 (Plan 110-09, D-07) — a one-off, per-project percentage
  // override, keyed by project id as a string. Sparse: only projects with a
  // non-empty entered value are sent, and only for projects still selected.
  // The value REPLACES the configured percentage for this invoice alone.
  const [discountOverrides, setDiscountOverrides] = useState<Record<string, string>>({});
  // Quick task 260912-j8z (G-02) — the carried selection currently in
  // effect. Initialized from `carried` and DROPPED (never silently ignored
  // — see the effect below) the moment the project selection no longer
  // matches exactly the one project it was carried for. `inclusion` is
  // global to a request; a selection made for one project cannot describe a
  // two-project request.
  const [activeInclusion, setActiveInclusion] = useState<InclusionDescriptor | null>(
    carried?.inclusion ?? null,
  );
  const [inclusionDropped, setInclusionDropped] = useState(false);

  // ---- MKS-207 review panel ----
  // The panel is about ONE project and ONE month; anything else keeps the
  // page exactly as it was before this ticket.
  const reviewPeriod = useMemo(
    () => (form.from && form.to ? periodFromRange(form.from, form.to) : null),
    [form.from, form.to],
  );
  const reviewProjectId = form.projectIds.length === 1 ? form.projectIds[0] : undefined;
  const { data: reviewResponse } = useProjectToBeInvoiced(
    reviewProjectId,
    reviewPeriod ?? '',
    reviewPeriod !== null,
  );
  const reviewData = reviewResponse?.data;
  const [reviewSelection, setReviewSelection] = useState<ReviewSelection | null>(null);
  // The inclusion the held `preview` was produced with. Mirrors the Phase 110
  // discount-override gate: Generate must never run over a preview that does
  // not reflect the ticks on screen.
  const [previewedInclusionKey, setPreviewedInclusionKey] = useState<string | null>(null);
  // An explicit, per-selection acknowledgement that a 🔴 item is going on the
  // invoice anyway. Reset whenever the selection changes so it can never be
  // carried over to a different set of ticks.
  const [blockedConfirmed, setBlockedConfirmed] = useState(false);

  const reviewHasBlocked =
    reviewData != null && reviewSelection != null
      ? selectionHasBlockedItem(reviewData, reviewSelection)
      : false;
  const blockedUnconfirmed = reviewHasBlocked && !blockedConfirmed;

  // Non-blocking warning: flag projects whose preview total is 0 so a
  // mis-configured project (e.g. payment type not set yet, like the Hamty SLA
  // case) isn't silently invoiced for nothing. Fixed-price kinds legitimately
  // show 0 in the ledger (billed manually) — exclude them to avoid false alarms.
  const zeroAmountProjects = useMemo(
    () =>
      (preview?.byProject ?? []).filter(
        (p) =>
          p.totalBilling === 0 &&
          p.kind !== 'fixed_price' &&
          p.kind !== 'fixed_price_excluded',
      ),
    [preview],
  );

  // Sync form with URL params on mount
  useEffect(() => {
    const hasUrlData = urlParams.projectIds.length > 0 || urlParams.from || urlParams.to;
    if (hasUrlData) {
      setForm({
        projectIds: urlParams.projectIds,
        from: urlParams.from,
        to: urlParams.to,
        name: '',
        invoiceNumber: '',
        orderNumber: '',
        dueDate: '',
      });
      // MKS-256 — this resets `dueDate` to '', so the touched flag must reset
      // with it: whenever code empties the field, control returns to the
      // preview, and the flag must never outlive the value it describes.
      dueDateTouchedRef.current = false;
      setIsInitialized(true);
    }
  }, [urlParams]);

  // Quick task 260912-j8z (G-02) — discard-and-say-so. The moment the
  // project selection is anything other than exactly the one project the
  // carried selection describes, drop it and flip the visible notice. Never
  // drop it silently: `inclusionDropped` is the only signal the operator
  // gets that the invoice reverted to "bill everything uninvoiced".
  useEffect(() => {
    // Skip the very first render: `form.projectIds` still holds its initial
    // `[]` there, one tick before the URL-sync effect above populates it —
    // evaluating against that transient state would drop a just-carried
    // selection before it ever took effect.
    if (!isInitialized) return;
    if (activeInclusion === null) return;
    if (!carried) return;
    const stillExact = form.projectIds.length === 1 && form.projectIds[0] === carried.forProjectId;
    if (!stillExact) {
      setActiveInclusion(null);
      setInclusionDropped(true);
    }
    // `carried` is stable for the life of this URL (decoded once from
    // `searchParams`); only `form.projectIds` actually changes over time.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [form.projectIds]);

  // Auto-preview when form is initialized with all required params
  useEffect(() => {
    if (
      isInitialized &&
      form.projectIds.length > 0 &&
      form.from &&
      form.to &&
      !preview &&
      !previewMutation.isPending
    ) {
      // This effect deliberately sends no `discountOverrides` at all, so the
      // key records the empty payload that was actually transmitted — not
      // whatever `discountOverrides` state happens to hold. At this moment
      // the map is necessarily empty anyway (the override row cannot render
      // before a preview exists), and if that ever stopped being true the
      // mismatch would disable Generate until the operator previews, which
      // is the fail-safe direction.
      previewMutation.mutateAsync({
        projectIds: form.projectIds,
        from: form.from,
        to: form.to,
        // Quick task 260912-j8z (G-02) — a carried selection must be visible
        // on the FIRST preview, not only after a manual re-Preview; the
        // operator should never see "everything" for a moment before the
        // real, restricted breakdown replaces it.
        inclusion: activeInclusion ?? undefined,
        billingPeriodIds: billingPeriodIdsPayload,
      }).then((result) => {
        setPreview(result);
        applyDueDateSuggestion(result);
        setPreviewedOverridesKey(discountOverridesKey(undefined));
        setPreviewedInclusionKey(inclusionKey(activeInclusion));
      }).catch((err) => {
        setError((err as Error).message || 'Preview failed.');
      });
    }
    // Only run once when initialized
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isInitialized]);

  // MKS-207 — seed the review selection once the payload for THIS project and
  // period arrives. A selection carried from the project card wins (the
  // operator already made those choices there); otherwise everything that is
  // not 🔴 starts ticked. Re-seeds when the project or the period changes,
  // never on an unrelated re-render — `reviewData.period` and the project id
  // are the identity of what is on screen.
  useEffect(() => {
    if (!reviewData) {
      setReviewSelection(null);
      return;
    }
    const seeded = defaultSelection(reviewData);
    const carriedForThisProject =
      carried != null && carried.forProjectId === reviewProjectId ? carried.inclusion : null;
    const next: ReviewSelection = carriedForThisProject
      ? {
          package: carriedForThisProject.package ?? seeded.package,
          overage: carriedForThisProject.overage ?? seeded.overage,
          expenseReceiptIds: carriedForThisProject.expenseReceiptIds ?? seeded.expenseReceiptIds,
          entryIds: carriedForThisProject.entryIds,
        }
      : seeded;
    setReviewSelection(next);
    setActiveInclusion(toInclusion(next));
    setBlockedConfirmed(false);
    // `carried` is decoded once per URL and stable for the life of the page.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [reviewProjectId, reviewData?.period, reviewData != null]);

  // MKS-207 — "changing a checkbox updates the previewed total": re-preview
  // whenever the inclusion the panel produced differs from the one the held
  // preview was built with. Keyed on the canonical inclusion key rather than
  // object identity, so a re-render that produces an equal selection does not
  // fire a request.
  const activeInclusionKey = inclusionKey(activeInclusion);
  useEffect(() => {
    if (!reviewData || !reviewSelection) return;
    // Deliberately NOT gated on `isInitialized`: that flag only turns true
    // when the page was entered with URL parameters (the hop from the project
    // card). An operator who picks the project and the month by hand must get
    // live totals too, and before MKS-207 nothing on this page did.
    if (!form.from || !form.to || form.projectIds.length !== 1) return;
    if (previewMutation.isPending) return;
    if (previewedInclusionKey === activeInclusionKey) return;

    const overridesPayload = buildDiscountOverridesPayload();
    previewMutation
      .mutateAsync({
        projectIds: form.projectIds,
        from: form.from,
        to: form.to,
        discountOverrides: overridesPayload,
        inclusion: activeInclusion ?? undefined,
      })
      .then((result) => {
        setPreview(result);
        applyDueDateSuggestion(result);
        setPreviewedOverridesKey(discountOverridesKey(overridesPayload));
        setPreviewedInclusionKey(activeInclusionKey);
      })
      .catch((err) => {
        setError((err as Error).message || 'Preview failed.');
      });
    // `previewMutation.isPending` is a dependency, not just a guard: without
    // it a selection change that lands while another preview is in flight
    // would bail out and never retry, leaving the totals stale.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    activeInclusionKey,
    reviewData,
    reviewSelection,
    form.from,
    form.to,
    form.projectIds,
    previewMutation.isPending,
  ]);

  const toggleProject = (id: number) => {
    setForm((prev) => ({
      ...prev,
      projectIds: prev.projectIds.includes(id)
        ? prev.projectIds.filter((pid) => pid !== id)
        : [...prev.projectIds, id],
    }));
    setPreview(null);
  };

  // Phase 110 (Plan 110-12, 110-REVIEW CR-02) — invalidation deliberately
  // does NOT happen here. The override row's existence is derived from
  // `preview` (see `showOverrideRow` in `Section` below), so discarding the
  // preview on a keystroke would unmount the input mid-typing. The
  // freshness gate below (`previewOverridesStale`) is computed from the
  // payload instead, which flips on the same keystroke without destroying
  // the field.
  const handleOverrideChange = (projectId: number, value: string) => {
    setDiscountOverrides((prev) => ({ ...prev, [String(projectId)]: value }));
  };

  // Phase 110 (Plan 110-09, D-07) — sparse map, restricted to currently
  // selected projects with a non-empty entered value. Deselecting a project
  // does not delete its entered value from state (so re-selecting keeps it),
  // but it never reaches the payload while the project is unselected.
  const buildDiscountOverridesPayload = (): Record<string, string> | undefined => {
    const entries = Object.entries(discountOverrides).filter(
      ([projectIdStr, value]) =>
        value.trim() !== '' && form.projectIds.includes(Number(projectIdStr)),
    );
    return entries.length > 0 ? Object.fromEntries(entries) : undefined;
  };

  // Client-side mirror of the server-side bound (see DISCOUNT_OVERRIDE_PATTERN
  // above) — never a replacement for it. Returns the first offending
  // project's error banner text, or null when every entered override is
  // valid.
  const validateDiscountOverrides = (): string | null => {
    for (const [projectIdStr, value] of Object.entries(discountOverrides)) {
      const projectId = Number(projectIdStr);
      if (!form.projectIds.includes(projectId)) continue;
      if (value.trim() === '') continue;
      if (!DISCOUNT_OVERRIDE_PATTERN.test(value.trim())) {
        const project = projects?.find((p) => p.id === projectId);
        const projectName = project?.fullName || project?.name || `Project ${projectId}`;
        return `${projectName}: override must be a whole number from 0 to 100.`;
      }
    }
    return null;
  };

  // Phase 110 (Plan 110-12, 110-REVIEW CR-02) — the freshness gate. Both
  // consts are derived on every render from `buildDiscountOverridesPayload`,
  // the same function that fills both mutation bodies, so the gate is a
  // property of the payload rather than a flag a future edit could forget to
  // set. Plain consts, not `useMemo` — the payload is a handful of entries
  // and a memo here would only add a dependency list to get wrong.
  // Invariant enforced: Generate can never be enabled over a preview that
  // does not reflect the override values which will be sent.
  const currentOverridesKey = discountOverridesKey(buildDiscountOverridesPayload());
  const previewOverridesStale = preview !== null && previewedOverridesKey !== currentOverridesKey;

  const handlePreview = async () => {
    setError(null);
    if (form.projectIds.length === 0) {
      setError('Select at least one project.');
      return;
    }
    if (!form.from || !form.to) {
      setError('Both from and to dates are required.');
      return;
    }
    const overrideError = validateDiscountOverrides();
    if (overrideError) {
      setError(overrideError);
      return;
    }
    // Hoisted into a single const and reused for both the request and the
    // key: calling the builder twice here is the one mistake that would
    // silently defeat the whole gate.
    const overridesPayload = buildDiscountOverridesPayload();
    try {
      const result = await previewMutation.mutateAsync({
        projectIds: form.projectIds,
        from: form.from,
        to: form.to,
        discountOverrides: overridesPayload,
        // Quick task 260912-j8z (G-02) — the carried selection, if one is
        // still in effect (see the discard effect above). `undefined` when
        // nothing was carried or it was dropped — today's behaviour.
        inclusion: activeInclusion ?? undefined,
        // MKS-136 — so the previewed Fixed Price draw-down is the period's own
        // amount, which is what generation will actually draw.
        billingPeriodIds: billingPeriodIdsPayload,
      });
      setPreview(result);
      applyDueDateSuggestion(result);
      setPreviewedOverridesKey(discountOverridesKey(overridesPayload));
      setPreviewedInclusionKey(inclusionKey(activeInclusion));
    } catch (err) {
      setError((err as Error).message || 'Preview failed.');
    }
  };

  const handleGenerate = async () => {
    setError(null);
    const overrideError = validateDiscountOverrides();
    if (overrideError) {
      setError(overrideError);
      return;
    }
    // MKS-207 — the blocked-item gate, enforced on the dispatch path and not
    // only through the Generate button's `disabled` attribute. A 🔴 item bills
    // at zero; issuing one has to be a deliberate act.
    if (blockedUnconfirmed) {
      setError(BLOCKED_CONFIRM_MESSAGE);
      return;
    }
    // Defence-in-depth guard (Phase 110, Plan 110-12, 110-REVIEW CR-02): the
    // disabled attribute on the Generate button is a UI affordance, not a
    // control. This guard recomputes the key from the very payload it is
    // about to send and refuses on disagreement, making the invariant true
    // of the dispatch path itself.
    const overridesPayload = buildDiscountOverridesPayload();
    if (discountOverridesKey(overridesPayload) !== previewedOverridesKey) {
      setError(STALE_PREVIEW_MESSAGE);
      return;
    }
    try {
      await generateMutation.mutateAsync({
        projectIds: form.projectIds,
        from: form.from,
        to: form.to,
        name: form.name || undefined,
        invoiceNumber: form.invoiceNumber || undefined,
        orderNumber: form.orderNumber || undefined,
        dueDate: form.dueDate || undefined,
        discountOverrides: overridesPayload,
        // Quick task 260912-j8z (G-02) — same rule as the preview call
        // above: what was confirmed on screen is what gets billed.
        inclusion: activeInclusion ?? undefined,
        // MKS-136 — link these periods to the issued invoice and flip them to
        // `invoiced`. Periods nobody ticked stay ready and come back next time.
        billingPeriodIds: billingPeriodIdsPayload,
      });
      navigate('/invoices');
    } catch (err) {
      setError((err as Error).message || 'Generation failed.');
    }
  };

  return (
    <div className="space-y-6">
      <div>
        <button onClick={() => navigate('/invoices')} className="text-sm text-gray-500 hover:text-gray-700 mb-1">
          &larr; Back to Invoices
        </button>
        <h1 className="text-2xl font-semibold text-gray-900">Generate Invoice</h1>
      </div>

      {error && (
        <div className="bg-red-50 border border-red-200 rounded-lg p-3">
          <p className="text-sm text-red-800">{error}</p>
        </div>
      )}

      <div className="bg-white rounded-lg border border-gray-200 p-6 space-y-5">
        {/* Period + Date Range */}
        <div className="grid grid-cols-1 md:grid-cols-[auto_1fr_1fr] gap-4 items-end">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Period</label>
            <select
              value=""
              onChange={(e) => {
                if (!e.target.value) return;
                const [year, month] = e.target.value.split('-').map(Number);
                const first = `${year}-${String(month).padStart(2, '0')}-01`;
                const lastDay = new Date(year!, month!, 0).getDate();
                const last = `${year}-${String(month).padStart(2, '0')}-${String(lastDay).padStart(2, '0')}`;
                setForm((prev) => ({ ...prev, from: first, to: last }));
                setPreview(null);
              }}
              className="w-full rounded-lg border border-gray-300 px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-primary-500"
            >
              <option value="">Select month...</option>
              {(() => {
                const options = [];
                const now = new Date();
                for (let i = 0; i < 12; i++) {
                  const d = new Date(now.getFullYear(), now.getMonth() - i, 1);
                  const value = `${d.getFullYear()}-${d.getMonth() + 1}`;
                  const label = formatMonthYear(d); // MKS-351 — "October 2026"
                  options.push(<option key={value} value={value}>{label}</option>);
                }
                return options;
              })()}
            </select>
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">From</label>
            <DateField
              value={form.from}
              onChange={(nextDate) => { setForm((prev) => ({ ...prev, from: nextDate })); setPreview(null); }}
              className="w-full rounded-lg border border-gray-300 px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-primary-500"
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">To</label>
            <DateField
              value={form.to}
              onChange={(nextDate) => { setForm((prev) => ({ ...prev, to: nextDate })); setPreview(null); }}
              className="w-full rounded-lg border border-gray-300 px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-primary-500"
            />
          </div>
        </div>

        {/* MKS-136 — which billing period this invoice is for. Ticking one
            sets the date range to the period's own billed range, which for a
            phase period starts after the PREVIOUS phase actually ended and so
            can span several months — the range no month picker can express.
            On a Fixed Price project the ticked period's amount becomes the
            draw-down, instead of the whole remaining contract balance. */}
        {readyPeriods.length > 0 && (
          <div className="rounded-lg border border-gray-200 px-4 py-3">
            <p className="text-sm font-medium text-gray-700">Billing period</p>
            <p className="mt-0.5 text-xs text-gray-500">
              Ticking a period links this invoice to it and marks it invoiced. Periods you leave
              unticked stay in the queue.
            </p>
            <div className="mt-2 space-y-1">
              {readyPeriods.map((period) => (
                <label key={period.id} className="flex items-center gap-2 text-sm text-gray-800">
                  <input
                    type="checkbox"
                    checked={billingPeriodIds.includes(period.id)}
                    onChange={(e) => {
                      const next = e.target.checked
                        ? [...billingPeriodIds, period.id]
                        : billingPeriodIds.filter((id) => id !== period.id);
                      setSelectedPeriodIds(next);
                      setPreview(null);
                      // One period ticked answers the date range outright; two
                      // or more have no single range, so the operator's own
                      // dates are left alone.
                      if (e.target.checked && next.length === 1) {
                        setForm((prev) => ({
                          ...prev,
                          from: period.billedFrom,
                          to: period.billedTo,
                        }));
                      }
                    }}
                  />
                  <span>{period.name}</span>
                  <span className="text-xs text-gray-500">
                    {formatDisplayDate(period.billedFrom)} – {formatDisplayDate(period.billedTo)}
                    {period.plannedAmount !== null &&
                      ` · ${formatCurrency(period.plannedAmount, false, period.currency ?? 'CZK')}`}
                  </span>
                </label>
              ))}
            </div>
          </div>
        )}

        {/* Quick task 260912-j8z (G-02) — the carried-selection statement and
            its discard notice. The operator must be able to see, on this
            page, what was configured on the project card before it is
            billed — that is the entire point of the hop. */}
        {activeInclusion && !reviewData && (
          <div
            className="rounded-lg border border-blue-200 bg-blue-50 px-4 py-3 text-sm text-blue-800"
            role="status"
          >
            A billing selection was carried from the project page:{' '}
            {describeInclusion(activeInclusion)}.
          </div>
        )}
        {inclusionDropped && (
          <div
            className="rounded-lg border border-amber-300 bg-amber-50 px-4 py-3 text-sm text-amber-800"
            role="status"
          >
            The carried billing selection no longer applies because the project selection
            changed — this invoice will include everything uninvoiced in the period.
          </div>
        )}

        {/* Project Selection */}
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-2">
            Projects {form.projectIds.length > 0 && `(${form.projectIds.length} selected)`}
          </label>
          {/* Show warning if selected IDs don't match any project in list */}
          {form.projectIds.length > 0 && projects && (() => {
            const projectIdSet = new Set(projects.map(p => p.id));
            const unmatchedIds = form.projectIds.filter(id => !projectIdSet.has(id));
            if (unmatchedIds.length > 0) {
              return (
                <div className="mb-2 px-3 py-2 bg-yellow-50 border border-yellow-200 rounded-lg">
                  <p className="text-sm text-yellow-800">
                    Selected project IDs not found in list: {unmatchedIds.join(', ')}
                  </p>
                </div>
              );
            }
            return null;
          })()}
          <div className="border border-gray-200 rounded-lg">
            <div className="p-2 border-b border-gray-200 flex flex-col md:flex-row gap-2">
              <input
                type="text"
                value={projectFilter}
                onChange={(e) => setProjectFilter(e.target.value)}
                placeholder="Filter projects..."
                className="flex-1 rounded border border-gray-300 px-2 py-1.5 text-sm focus:outline-none focus:ring-2 focus:ring-primary-500"
              />
              <div className="inline-flex rounded-md border border-gray-300 overflow-hidden text-xs">
                {(
                  [
                    { v: 'all', label: 'All' },
                    { v: 'balicek', label: 'SLA Hour Package' },
                    { v: 'hourly', label: 'Hourly' },
                  ] as { v: BillingFilter; label: string }[]
                ).map((opt) => (
                  <button
                    key={opt.v}
                    type="button"
                    onClick={() => setBillingFilter(opt.v)}
                    className={`px-3 py-1.5 ${
                      billingFilter === opt.v
                        ? 'bg-primary-600 text-white'
                        : 'bg-white text-gray-700 hover:bg-gray-50'
                    }`}
                  >
                    {opt.label}
                  </button>
                ))}
              </div>
            </div>
            <div className="max-h-72 overflow-y-auto">
              <ProjectPickerSections
                projects={projects ?? []}
                projectFilter={projectFilter}
                billingFilter={billingFilter}
                selectedIds={form.projectIds}
                onToggle={toggleProject}
                preview={preview}
                discountOverrides={discountOverrides}
                onOverrideChange={handleOverrideChange}
              />
            </div>
          </div>
        </div>

        {/* Optional Fields */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Invoice Name</label>
            <input
              type="text"
              value={form.name}
              onChange={(e) => setForm((prev) => ({ ...prev, name: e.target.value }))}
              placeholder="Auto-generated if empty"
              className="w-full rounded-lg border border-gray-300 px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-primary-500"
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Invoice Number</label>
            <input
              type="text"
              value={form.invoiceNumber}
              onChange={(e) => setForm((prev) => ({ ...prev, invoiceNumber: e.target.value }))}
              placeholder="Optional"
              className="w-full rounded-lg border border-gray-300 px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-primary-500"
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Order Number</label>
            <input
              type="text"
              value={form.orderNumber}
              onChange={(e) => setForm((prev) => ({ ...prev, orderNumber: e.target.value }))}
              placeholder="Optional"
              className="w-full rounded-lg border border-gray-300 px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-primary-500"
            />
          </div>
          {/* MKS-214 — editable at issue time. The automatic default (issue
              date + the project's invoice due days) arrives on the preview as
              `defaultDueDate`, and `applyDueDateSuggestion` above keeps this
              field tracking it while it is untouched (MKS-256). */}
          <div>
            <label htmlFor="generate-due-date" className="block text-sm font-medium text-gray-700 mb-1">
              Due Date
            </label>
            <DateField
              id="generate-due-date"
              value={form.dueDate}
              onChange={(nextDate) => {
                // MKS-256 — the only place that marks the field touched, and
                // the escape hatch: clearing it (`nextDate === ''`) hands
                // control back to the preview, so the next result re-suggests.
                dueDateTouchedRef.current = nextDate !== '';
                setForm((prev) => ({ ...prev, dueDate: nextDate }));
              }}
              className="w-full rounded-lg border border-gray-300 px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-primary-500"
            />
          </div>
        </div>

        {/* Phase 110 (Plan 110-12, 110-REVIEW CR-02) — stale-preview notice.
            `role="status"` rather than `alert`: this is a state the operator
            caused and can undo, not an error. */}
        {previewOverridesStale && (
          <div
            className="rounded-lg border border-amber-300 bg-amber-50 px-4 py-3 text-sm text-amber-800"
            role="status"
          >
            {STALE_PREVIEW_MESSAGE}
          </div>
        )}

        {/* MKS-207 — the blocked-item confirmation. It appears only while a
            🔴 item is actually ticked, and unticks itself the moment the
            selection changes. */}
        {reviewHasBlocked && (
          <div
            className="rounded-lg border border-red-300 bg-red-50 px-4 py-3 text-sm text-red-800"
            role="alert"
          >
            <p>{BLOCKED_CONFIRM_MESSAGE}</p>
            <label className="mt-2 flex items-center gap-2">
              <input
                type="checkbox"
                checked={blockedConfirmed}
                onChange={(e) => setBlockedConfirmed(e.target.checked)}
                className="rounded border-gray-300 text-primary-600 focus:ring-primary-500"
              />
              <span>Yes, invoice the blocked item anyway.</span>
            </label>
          </div>
        )}

        {/* Actions */}
        <div className="flex gap-3 pt-4 border-t border-gray-100">
          <Button variant="secondary" onClick={handlePreview} isLoading={previewMutation.isPending}>
            Preview
          </Button>
          <Button
            onClick={handleGenerate}
            isLoading={generateMutation.isPending}
            disabled={
              !preview ||
              preview.byProject.length === 0 ||
              previewMutation.isPending ||
              previewOverridesStale ||
              blockedUnconfirmed ||
              !isFinance
            }
          >
            Generate Invoice
          </Button>
        </div>
      </div>

      {/* MKS-207 — the review panel: one project, one month, every billable
          item with its state and a checkbox. */}
      {reviewData && reviewSelection && reviewProjectId != null && (
        <InvoiceReviewPanel
          projectId={reviewProjectId}
          data={reviewData}
          selection={reviewSelection}
          canManage={isFinance}
          previewTotal={
            preview?.byProject.find((p) => p.projectId === reviewProjectId)?.totalBilling ?? null
          }
          previewDiscount={
            preview?.byProject.find((p) => p.projectId === reviewProjectId)?.discount?.amountCzk
          }
          onSelectionChange={(next) => {
            setReviewSelection(next);
            setActiveInclusion(toInclusion(next));
            setBlockedConfirmed(false);
          }}
        />
      )}

      {/* Non-blocking zero-amount warning (generation stays allowed). */}
      {zeroAmountProjects.length > 0 && (
        <div
          className="mt-4 rounded-lg border border-amber-300 bg-amber-50 px-4 py-3 text-sm text-amber-800"
          role="alert"
        >
          <strong>Heads up:</strong> {zeroAmountProjects.length} project
          {zeroAmountProjects.length > 1 ? 's' : ''} will be invoiced for 0 — check the payment
          type and rates before generating:
          <ul className="mt-1 list-inside list-disc">
            {zeroAmountProjects.map((p) => (
              <li key={p.projectId}>{p.projectName}</li>
            ))}
          </ul>
        </div>
      )}

      {/* Preview Breakdown */}
      {preview && <InvoiceBreakdownTable breakdown={preview} title="Invoice Preview" />}
    </div>
  );
}

/**
 * UAT 2026-05-13 — split project picker into 'SLA Hour Package' + 'Hourly' sections
 * so PMs invoicing a mixed batch can see at a glance which payment template
 * each project is on. The billing filter (All / SLA Hour Package / Hourly) above
 * narrows the visible sections; the name filter (top input) further filters
 * inside each section. Empty sections render a one-line hint instead of
 * disappearing — keeps the layout stable as the user toggles filters.
 */
function ProjectPickerSections({
  projects,
  projectFilter,
  billingFilter,
  selectedIds,
  onToggle,
  preview,
  discountOverrides,
  onOverrideChange,
}: {
  projects: ProjectListItem[];
  projectFilter: string;
  billingFilter: BillingFilter;
  selectedIds: number[];
  onToggle: (id: number) => void;
  preview: InvoiceBreakdown | null;
  discountOverrides: Record<string, string>;
  onOverrideChange: (projectId: number, value: string) => void;
}) {
  const nameMatches = (p: ProjectListItem): boolean => {
    if (!projectFilter) return true;
    return (p.fullName || p.name || '').toLowerCase().includes(projectFilter.toLowerCase());
  };
  const filtered = projects.filter(nameMatches);
  const balicek = filtered.filter((p) => p.paymentTypeId === SLA_BALICEK_PAYMENT_TYPE_ID);
  const hourly = filtered.filter((p) => p.paymentTypeId !== SLA_BALICEK_PAYMENT_TYPE_ID);

  const showBalicek = billingFilter === 'all' || billingFilter === 'balicek';
  const showHourly = billingFilter === 'all' || billingFilter === 'hourly';

  if (projects.length === 0) {
    return <p className="text-sm text-gray-500 px-3 py-2">No projects available.</p>;
  }
  if (filtered.length === 0) {
    return <p className="text-sm text-gray-500 px-3 py-2">No projects match the filter.</p>;
  }

  return (
    <div>
      {showBalicek && (
        <Section
          title="SLA Hour Package"
          tone="blue"
          projects={balicek}
          selectedIds={selectedIds}
          onToggle={onToggle}
          emptyHint="No SLA Hour Package projects in the current filter."
          preview={preview}
          discountOverrides={discountOverrides}
          onOverrideChange={onOverrideChange}
        />
      )}
      {showHourly && (
        <Section
          title="Hourly"
          tone="indigo"
          projects={hourly}
          selectedIds={selectedIds}
          onToggle={onToggle}
          emptyHint="No hourly projects in the current filter."
          preview={preview}
          discountOverrides={discountOverrides}
          onOverrideChange={onOverrideChange}
        />
      )}
    </div>
  );
}

function Section({
  title,
  tone,
  projects,
  selectedIds,
  onToggle,
  emptyHint,
  preview,
  discountOverrides,
  onOverrideChange,
}: {
  title: string;
  tone: 'blue' | 'indigo';
  projects: ProjectListItem[];
  selectedIds: number[];
  onToggle: (id: number) => void;
  emptyHint: string;
  preview: InvoiceBreakdown | null;
  discountOverrides: Record<string, string>;
  onOverrideChange: (projectId: number, value: string) => void;
}) {
  const selectedCount = projects.filter((p) => selectedIds.includes(p.id)).length;
  const chipBg = tone === 'blue' ? 'bg-blue-100 text-blue-800' : 'bg-indigo-100 text-indigo-800';
  return (
    <div className="border-b border-gray-100 last:border-b-0">
      <div className="px-3 py-1.5 bg-gray-50 flex items-center justify-between sticky top-0">
        <div className="flex items-center gap-2">
          <span className={`inline-flex items-center px-2 py-0.5 rounded text-[10.5px] font-medium ${chipBg}`}>
            {title}
          </span>
          <span className="text-xs text-gray-500">{projects.length} projects</span>
        </div>
        {selectedCount > 0 && (
          <span className="text-xs font-medium text-gray-700">
            {selectedCount} selected
          </span>
        )}
      </div>
      <div className="p-2 space-y-1">
        {projects.length === 0 ? (
          <p className="text-xs text-gray-400 italic px-2 py-1">{emptyHint}</p>
        ) : (
          projects.map((project) => {
            const isSelected = selectedIds.includes(project.id);
            // Phase 110 (Plan 110-09, D-07) — the override row appears only
            // for a project that is BOTH selected AND shown by the latest
            // preview to have a configured discount. The `discount` key is
            // absent on the breakdown row for mode 'none' or a resolved 0%
            // (per REQ-110-07, and `InvoiceProjectDiscount.mode` is typed as
            // never 'none' for exactly this reason) — so its presence alone
            // IS the "mode !== 'none'" signal this row's render condition
            // requires.
            //
            // MKS-206 (110-REVIEW WR-02) — that reasoning was only half the
            // story: `resolveDiscount` ALSO omits the block when a configured
            // discount resolves to 0% (a tiers project below its first
            // threshold, one with no tier rows, a manual 0%), while the backend
            // accepts an override on exactly those rows. The row then carries
            // `discountAvailable` instead, so either key means "this project
            // has a discount configuration" and the operator can reach the
            // one-off override the server was always willing to take.
            const breakdownRow = preview?.byProject.find((p) => p.projectId === project.id);
            const discount = breakdownRow?.discount;
            const available = breakdownRow?.discountAvailable;
            const showOverrideRow = isSelected && (discount != null || available != null);
            // What the configuration alone resolves to, whichever key carried it.
            const configuredPercentage =
              discount != null
                ? (discount.configuredPercentage ?? discount.percentage)
                : available?.configuredPercentage;
            const configuredMode = discount?.mode ?? available?.mode;

            return (
              <div key={project.id}>
                <label className="flex items-center gap-2 px-2 py-1.5 rounded hover:bg-gray-50 cursor-pointer">
                  <input
                    type="checkbox"
                    checked={isSelected}
                    onChange={() => onToggle(project.id)}
                    className="rounded border-gray-300 text-primary-600 focus:ring-primary-500"
                  />
                  <span className="text-sm text-gray-900">{project.fullName || project.name}</span>
                </label>
                {showOverrideRow && (
                  <div className="pl-8 pb-1.5 flex flex-wrap items-center gap-2 text-xs text-gray-500">
                    <span>
                      Configured discount: {configuredPercentage}% ({configuredMode})
                      {discount == null && ' — nothing applies this period'}
                    </span>
                    <label className="flex items-center gap-1.5">
                      <span>Override for this invoice:</span>
                      <input
                        type="text"
                        inputMode="numeric"
                        value={discountOverrides[String(project.id)] ?? ''}
                        onChange={(e) => onOverrideChange(project.id, e.target.value)}
                        placeholder="—"
                        className="w-16 rounded border border-gray-300 px-2 py-1 text-sm"
                      />
                      <span>%</span>
                    </label>
                  </div>
                )}
              </div>
            );
          })
        )}
      </div>
    </div>
  );
}
